import ReactECharts from 'echarts-for-react';

/**
 * BotCareerPathChart_4 — CONTENT ALTERNATIVE to BotCareerPathChart.
 *
 * Same real data (autonomy vs. human review across the 5 lifecycle stages),
 * but the FINDING is the crossover: the shaded lens between the two lines and
 * the exact "handover point" where bot autonomy overtakes human review —
 * interpolated between 'Reviewed' and 'Semi-Auto' from the original values.
 * Angle: the handover point — where the bot earns the wheel.
 *
 * Numeric x-axis (stage index 0..4) so the interpolated crossover point can sit
 * at its true position between categories. Opaque white editorial card.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealDark: '#087B8B',
  success: '#10B981',
  warning: '#F59E0B',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const STAGES = ['Hired', 'Probation', 'Reviewed', 'Semi-Auto', 'Full-Auto'];
// Real values, verbatim from BotCareerPathChart.
const AUTONOMY = [0, 10, 30, 60, 90];
const REVIEW = [100, 90, 70, 40, 10];

/** ONE shared percentage formatter — tooltip, axis ticks, and point labels all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

// --- Derived crossover (plain arithmetic on the real values above) ----------
// Between 'Reviewed' (index 2) and 'Semi-Auto' (index 3) the lead flips:
// at Reviewed review leads by 40 pts, at Semi-Auto autonomy leads by 20 pts.
const IDX_BEFORE = 2; // 'Reviewed'
const IDX_AFTER = 3; // 'Semi-Auto'
const LEAD_BEFORE = REVIEW[IDX_BEFORE] - AUTONOMY[IDX_BEFORE]; // 40
const LEAD_AFTER = AUTONOMY[IDX_AFTER] - REVIEW[IDX_AFTER]; // 20
// Linear interpolation: fraction of the stage interval where the lines meet.
const CROSS_T = LEAD_BEFORE / (LEAD_BEFORE + LEAD_AFTER); // 40 / 60 = 2/3
const CROSS_X = IDX_BEFORE + CROSS_T; // 2.667 — two-thirds into Semi-Auto
const CROSS_Y = AUTONOMY[IDX_BEFORE] + CROSS_T * (AUTONOMY[IDX_AFTER] - AUTONOMY[IDX_BEFORE]); // 50

/** Stage label for a numeric x position; non-integer x is the handover point. */
const stageLabel = (x: number) => (Number.isInteger(x) && STAGES[x] ? STAGES[x] : 'Handover point');

// Series data as [x, y] pairs, with the interpolated crossover inserted so both
// lines pass through it exactly (straight segments, no smoothing).
const toPoints = (values: number[]) => {
  const pts = values.map((v, i) => [i, v] as [number, number]);
  pts.splice(IDX_AFTER, 0, [CROSS_X, CROSS_Y]);
  return pts;
};
const autonomyPts = toPoints(AUTONOMY);
const reviewPts = toPoints(REVIEW);
// Stacked-area trick for the lens between the lines: transparent base at
// min(a, r) and a teal band of |a − r| on top. At the crossover the band = 0,
// so the lens pinches shut exactly at the handover point.
const lensBasePts = autonomyPts.map(([x, a], i) => [x, Math.min(a, reviewPts[i][1])] as [number, number]);
const lensBandPts = autonomyPts.map(([x, a], i) => [x, Math.abs(a - reviewPts[i][1])] as [number, number]);

export const BotCareerPathChart_4 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: [number, number]; marker?: string }[]) => {
        const main = params.filter((p) => p.seriesName === 'Autonomy (bot acts)' || p.seriesName === 'Human review (gates)');
        if (!main.length) return '';
        const x = main[0].value[0];
        return `${stageLabel(x)}<br/>${main
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value[1])}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 48, right: 40, top: 48, bottom: 40 },
    xAxis: {
      type: 'value',
      min: 0,
      max: STAGES.length - 1,
      interval: 1,
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.muted, fontSize: 12, fontWeight: 600, formatter: (v: number) => stageLabel(v) },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    series: [
      {
        // Transparent base of the stacked lens — carries no meaning on its own.
        name: 'lens-base',
        type: 'line',
        stack: 'lens',
        symbol: 'none',
        silent: true,
        data: lensBasePts,
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        emphasis: { disabled: true },
      },
      {
        // The contested ground between the two lines (|autonomy − review|).
        name: 'Contested ground',
        type: 'line',
        stack: 'lens',
        symbol: 'none',
        silent: true,
        data: lensBandPts,
        lineStyle: { opacity: 0 },
        areaStyle: { color: BRAND.teal, opacity: 0.14 },
        emphasis: { disabled: true },
      },
      {
        name: 'Autonomy (bot acts)',
        type: 'line',
        smooth: false,
        symbolSize: 9,
        data: autonomyPts,
        itemStyle: { color: BRAND.success },
        lineStyle: { color: BRAND.success, width: 3 },
        label: {
          show: true,
          position: 'top',
          color: BRAND.success,
          fontWeight: 700,
          fontSize: 12,
          // Hide the label at the crossover point — the markPoint annotates it.
          formatter: (p: { value: [number, number] }) =>
            Number.isInteger(p.value[0]) ? formatPct(p.value[1]) : '',
        },
        markPoint: {
          symbol: 'pin',
          symbolSize: 46,
          itemStyle: { color: BRAND.teal },
          label: { show: false },
          data: [{ coord: [CROSS_X, CROSS_Y] }],
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: BRAND.tealDark, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: BRAND.tealDark,
            fontWeight: 700,
            fontSize: 11,
            formatter: `Handover point\nautonomy ${formatPct(CROSS_Y)} = review ${formatPct(CROSS_Y)}`,
          },
          data: [{ xAxis: CROSS_X }],
        },
      },
      {
        name: 'Human review (gates)',
        type: 'line',
        smooth: false,
        symbolSize: 9,
        data: reviewPts,
        itemStyle: { color: BRAND.warning },
        lineStyle: { color: BRAND.warning, width: 3, type: 'dashed' },
        label: {
          show: true,
          position: 'bottom',
          color: BRAND.warning,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: [number, number] }) =>
            Number.isInteger(p.value[0]) ? formatPct(p.value[1]) : '',
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The handover point — where the bot earns the wheel: between 'Reviewed' (30% autonomy / 70% review)
        and 'Semi-Auto' (60% / 40%), the lines cross at 50% — roughly two-thirds of the way into the semi-auto
        transition. The shaded lens is the contested ground where bot and human still split the work. Hover for
        values.
      </p>
    </div>
  );
};
